import React, { useState, useEffect, useRef, useContext } from 'react';
import { Modal, ModalBody, ModalHeader } from "../modal/Modal";
import examService from "../../services/ExamService";
import GLOBAL from "../../global";
import AppContext from "../../contexts/AppContext";
import { toast } from 'react-toastify';

const DeepDriveUploadModal = ({ isOpen, toggle, onSubmit, deepDriveId, deepDriveTitle }) => {
    const { userInfo } = useContext(AppContext);
    const [details, setDetails] = useState([]);
    const [instructions, setInstructions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [currentMediaId, setCurrentMediaId] = useState(null);

    useEffect(() => {
        if (isOpen && deepDriveId) {
            _getDeepDriveDetails();
        }
    }, [isOpen, deepDriveId]);

    const _getDeepDriveDetails = async () => {
        setIsLoading(true);
        const payload = {
            deepDriveId: deepDriveId,
            tenant: GLOBAL.TENANT
        };
        const response = await examService.getDeepDriveDetails(payload);
        const { data, success } = response;
        if (success && data) {
            const result = data.Message || data.message || data;
            setDetails(result.Details || []);
            setInstructions(result.Instruction || []);
        }
        setIsLoading(false);
    }

    const handleButtonClick = (item) => {
        if (isUploading) return;
        setCurrentMediaId(item.Id);
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleRemoveFile = (mediaId, e) => {
        if (e) e.stopPropagation();
        setUploadedFiles(prev => {
            const next = { ...prev };
            delete next[mediaId];
            return next;
        });
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file || !currentMediaId) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result.split(',')[1];
            const extension = file.name.split('.').pop();

            setUploadedFiles(prev => ({
                ...prev,
                [currentMediaId]: {
                    file,
                    base64: base64String,
                    extension,
                    fileName: file.name
                }
            }));
            toast.info(`Selected: ${file.name}`);
        };
        reader.readAsDataURL(file);

        // Reset input value to allow the same file to be uploaded again if needed
        event.target.value = '';
    };

    const handleSubmit = async () => {
        const mediaIds = Object.keys(uploadedFiles);
        if (mediaIds.length === 0) {
            toast.warn('Please select at least one video to upload');
            return;
        }

        setIsUploading(true);
        let successCount = 0;
        const patientId = userInfo?.ContactId || GLOBAL.URL_PARAMS.PATIENT_ID || userInfo?.Id || 0;

        for (const mediaId of mediaIds) {
            const { base64, extension } = uploadedFiles[mediaId];
            const payload = {
                deepDriveMediaId: parseInt(mediaId),
                mediaContent: base64,
                extension: extension,
                patientId: patientId.toString(),
                tenant: GLOBAL.TENANT,
                clientType: 2
            };

            try {
                const response = await examService.storeDeepDriveMedia(payload);
                // Check for success more robustly as requested
                if (response.success && (response.data?.Successful === true || response.data?.successful === true || response.data === true)) {
                    successCount++;
                } else {
                    const errorMsg = response.data?.DisplayMessage ||
                        response.data?.ErrorMessages?.[0] ||
                        response.data?.errorMessages?.[0] ||
                        (typeof response.data?.Message === 'string' ? response.data?.Message : null) ||
                        'Upload process failed for an item';
                    console.warn(`Upload failed for mediaId ${mediaId}:`, response);
                    toast.error(errorMsg);
                }
            } catch (error) {
                console.error(`Error uploading mediaId ${mediaId}:`, error);
                toast.error('An unexpected error occurred during upload');
            }
        }

        setIsUploading(false);

        if (successCount > 0) {
            if (successCount === mediaIds.length) {
                toast.success('All media items uploaded successfully');
            } else {
                toast.warning(`${successCount} of ${mediaIds.length} items uploaded successfully`);
            }
            // Trigger the parent's onSubmit which likely refreshes the parent list and closes the modal
            onSubmit && onSubmit();
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} className="modal-lg deep-drive-upload-modal">
            <ModalHeader toggle={toggle}>
                <div className="text-center w-100">
                    <h2 className="modal-title-custom">Upload Your {deepDriveTitle || 'Swing'} Videos</h2>
                </div>
            </ModalHeader>
            <ModalBody>
                {isLoading ? (
                    <div className="text-center p-5">
                        <div className="report-spinner mx-auto"></div>
                        <p>Loading details...</p>
                    </div>
                ) : (
                    <>
                        <div className="upload-container row">
                            {details && details.map((item, index) => (
                                <div className="col-12 col-md-6 upload-box-wrapper" key={item.Id || index}>
                                    <div className="upload-box">
                                        <div className="icon-placeholder">
                                            {item.MediaUrl ? (
                                                <img src={item.MediaUrl} alt={item.Title} className="dummy-image" />
                                            ) : (
                                                <svg viewBox="0 0 100 100" width="80" height="80">
                                                    <path d="M50 20 L40 40 L45 60 L40 80 M50 20 L60 40 L55 60 L60 80 M45 60 L55 60" stroke="#2079BC" fill="none" strokeWidth="2" />
                                                    <circle cx="50" cy="15" r="5" stroke="#2079BC" fill="none" strokeWidth="2" />
                                                    <path d="M40 80 L70 85" stroke="#2079BC" fill="none" strokeWidth="2" />
                                                </svg>
                                            )}
                                        </div>
                                        <h4 className="upload-title">{item.Title}</h4>
                                        <p className="upload-desc">
                                            {item.Description}
                                        </p>
                                        <div className="upload-btn-container">
                                            <button
                                                className={`upload-btn ${uploadedFiles[item.Id] ? 'file-selected' : ''}`}
                                                onClick={() => handleButtonClick(item)}
                                                disabled={isUploading}
                                            >
                                                {uploadedFiles[item.Id]
                                                    ? `Selected: ${uploadedFiles[item.Id].fileName}`
                                                    : 'Click to upload or drag & drop'}
                                            </button>
                                            {uploadedFiles[item.Id] && !isUploading && (
                                                <span
                                                    className="remove-upload-icon"
                                                    onClick={(e) => handleRemoveFile(item.Id, e)}
                                                    title="Remove selection"
                                                >
                                                    <i className="fa fa-times-circle"></i>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden-file-input"
                            onChange={handleFileChange}
                            accept="video/*,image/*"
                        />

                        <div className="instructions-section">
                            <h3 className="instructions-title">Instructions</h3>
                            {instructions && instructions.map((group, gIndex) => (
                                <div className="instruction-group" key={group.Id || gIndex}>
                                    <h5>
                                        {group.Icon && <i className={`${group.Icon} mr-2`}></i>}
                                        {" "}
                                        {group.Title}
                                    </h5>
                                    <ul>
                                        {group.Details && group.Details.map((detail, dIndex) => (
                                            <li key={detail.Id || dIndex}>{detail.Title}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>


                        <div className="text-center mt-3">
                            <button
                                className="submit-btn mt-3"
                                onClick={handleSubmit}
                                disabled={isUploading || !details || details.length === 0 || Object.keys(uploadedFiles).length < details.length}
                            >
                                {isUploading ? 'Uploading...' : 'Submit'}
                            </button>
                        </div>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

export default DeepDriveUploadModal;
